import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Footer from './Footer';
import Loader from '../common/Loader.js';
import axios from '../../config/axios';
import { buildPublicTrackPath } from '../../utils/publicTrackRoute.js';
import { Package, ArrowLeft, MapPin, Phone } from 'lucide-react';

const NAVBAR_HEIGHT = 80;

const hasFilledAmount = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const num = Number(value);
  return !Number.isNaN(num) && num !== 0;
};

const InfoField = ({ label, value }) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">{label}</p>
      <p className="font-medium text-gray-800 text-sm sm:text-base mt-1 break-words">{value}</p>
    </div>
  );
};

const statusBadge = (status) => {
  const colors = {
    booked: 'bg-blue-100 text-blue-800 border-blue-200',
    dispatched: 'bg-purple-100 text-purple-800 border-purple-200',
    received: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    delivered: 'bg-green-100 text-green-800 border-green-200',
    returned: 'bg-red-100 text-red-800 border-red-200',
  };
  return colors[status?.toLowerCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
};

const PublicViewShipment = () => {
  const navigate = useNavigate();
  const { shipmentId } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shipment, setShipment] = useState(null);

  useEffect(() => {
    const load = async () => {
      const accessToken =
        location.state?.accessToken ||
        sessionStorage.getItem(`track_access_${shipmentId}`) ||
        '';

      if (location.state?.shipment) {
        setShipment(location.state.shipment);
        if (accessToken) {
          sessionStorage.setItem(`track_access_${shipmentId}`, accessToken);
        }
        setLoading(false);
        return;
      }

      if (!accessToken) {
        setError('Please track the shipment first to view details.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const response = await axios.get(`/tracking/${encodeURIComponent(shipmentId)}/view`, {
          headers: { 'X-Track-Access-Token': accessToken },
        });
        if (response.data.success) {
          setShipment(response.data.shipment);
          sessionStorage.setItem(`track_access_${shipmentId}`, accessToken);
        } else {
          setError(response.data.error || 'Shipment not found');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load shipment details');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [shipmentId, location.state]);

  const backToTrack = () => {
    const key = shipment?.consignment_no || shipmentId;
    navigate(buildPublicTrackPath(key));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-purple-50/40 flex items-center justify-center" style={{ paddingTop: NAVBAR_HEIGHT }}>
        <Loader fullScreen={false} size="default" message="Loading shipment..." />
      </div>
    );
  }

  if (error || !shipment) {
    return (
      <div className="min-h-screen bg-purple-50/40 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4" style={{ paddingTop: NAVBAR_HEIGHT }}>
          <div className="bg-white rounded-2xl border border-red-200 shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
            <Package className="w-12 h-12 mx-auto text-red-400 mb-3" />
            <p className="text-red-700 font-semibold mb-2">Unable to view shipment</p>
            <p className="text-gray-600 text-sm mb-4">{error || 'Shipment not found'}</p>
            <button
              type="button"
              onClick={backToTrack}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-700 text-white rounded-full text-sm font-semibold hover:bg-purple-800 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Track
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const chargeRows = [
    ['Freight', shipment.freight_charges],
    ['Handling / Labour', shipment.handling_charges],
    ['Delivery', shipment.delivery_charges],
    ['Door Pickup', shipment.door_pickup_charges],
    ['Late', shipment.late_charges],
    ['Docket Fee', shipment.docket_fee],
    ['Other', shipment.other_charges],
    ['Insurance', shipment.insurance_charges],
    ['Discount', shipment.discount],
  ].filter(([, value]) => hasFilledAmount(value));

  return (
    <div className="min-h-screen bg-purple-50/40 flex flex-col">
      <section className="relative flex-1" style={{ paddingTop: NAVBAR_HEIGHT }}>
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-white/90 via-white/80 to-purple-50/70" />
        <div className="relative container mx-auto px-4 py-6 sm:py-8 max-w-5xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 flex items-center gap-2">
                <Package className="w-6 h-6 text-purple-700 shrink-0" />
                Shipment Details
              </h1>
              <p className="text-sm text-gray-500 mt-1">Read-only summary</p>
            </div>
            <button
              type="button"
              onClick={backToTrack}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-purple-200 bg-white text-purple-700 rounded-full text-sm font-semibold hover:bg-purple-50 transition w-full sm:w-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Track
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-3 sm:p-4 border-l-4 border-l-orange-500 col-span-2 sm:col-span-1">
              <p className="text-xs text-gray-500 font-semibold">GC Number</p>
              <p className="text-base sm:text-lg font-bold text-orange-600 font-mono break-all">{shipment.shipment_id}</p>
            </div>
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-3 sm:p-4 border-l-4 border-l-amber-500">
              <p className="text-xs text-gray-500 font-semibold">Consignment</p>
              <p className="text-base sm:text-lg font-bold text-amber-700 font-mono">{shipment.consignment_no || '—'}</p>
            </div>
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-3 sm:p-4 border-l-4 border-l-blue-500">
              <p className="text-xs text-gray-500 font-semibold mb-1">Status</p>
              <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold uppercase border ${statusBadge(shipment.status)}`}>
                {shipment.status}
              </span>
            </div>
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-3 sm:p-4 border-l-4 border-l-green-500">
              <p className="text-xs text-gray-500 font-semibold">Total Amount</p>
              <p className="text-base sm:text-lg font-bold text-green-600">
                ₹{Number(shipment.total_amount || 0).toFixed(2)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 sm:p-5">
              <h2 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-500" /> From
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoField label="Name" value={shipment.sender_name} />
                <InfoField label="Location" value={shipment.sender_location} />
                <InfoField label="Contact" value={shipment.sender_contact} />
                <InfoField label="Address" value={shipment.sender_address} />
              </div>
              {shipment.sender_contact && (
                <a
                  href={`tel:${shipment.sender_contact}`}
                  className="mt-3 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                >
                  <Phone className="w-3.5 h-3.5" />
                  {shipment.sender_contact}
                </a>
              )}
            </div>

            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 sm:p-5">
              <h2 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-500" /> To
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoField label="Name" value={shipment.receiver_name} />
                <InfoField label="Location" value={shipment.receiver_location} />
                <InfoField label="Contact" value={shipment.receiver_contact} />
                <InfoField label="Address" value={shipment.receiver_address} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 sm:p-5 mb-4">
            <h2 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200">Package</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <InfoField label="Articles" value={shipment.no_of_articles} />
              <InfoField label="Weight" value={shipment.weight != null ? `${shipment.weight} kg` : null} />
              <InfoField label="Vehicle" value={shipment.vehicle_number} />
              <InfoField
                label="Created"
                value={
                  shipment.created_at
                    ? new Date(shipment.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                      })
                    : null
                }
              />
            </div>
          </div>

          {(chargeRows.length > 0 || shipment.total_amount != null) && (
            <div className="bg-white rounded-xl border border-purple-100 shadow-sm p-4 sm:p-5 mb-4">
              <h2 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200">Charges</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {chargeRows.map(([label, value]) => (
                  <InfoField key={label} label={label} value={`₹${Number(value).toFixed(2)}`} />
                ))}
                <InfoField label="Paid" value={`₹${Number(shipment.paid_amount || 0).toFixed(2)}`} />
                <InfoField label="Balance" value={`₹${Number(shipment.balance_amount || 0).toFixed(2)}`} />
                <InfoField
                  label="Payment Mode"
                  value={
                    shipment.mode_of_payment
                      ? String(shipment.mode_of_payment).replace(/_/g, ' ')
                      : null
                  }
                />
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PublicViewShipment;
